/**
 * beaut-545. Pure helpers for the visit planning page.
 *
 * This file imports nothing on purpose. Anything reachable from
 * VisitAddBookingComponent drags in the @beautyapp/users barrel, whose Web
 * Worker uses `import.meta.url` — a construct the commonjs Jest transform
 * cannot parse, so a spec that touches the component dies before its first
 * assertion. Rules that matter get tested here instead.
 */

/**
 * Whether the operator may still choose who the visit is for.
 *
 * One booking carries exactly one employee and this page creates one booking,
 * so the choice closes as soon as a procedure is listed: the rows below were
 * priced, time-slotted and salary-split under the employee they were added
 * under, and swapping afterwards would leave the header disagreeing with every
 * one of them. A completed visit is closed to edits altogether.
 */
export function canChangeEmployee(v: {
  bookingCount: number;
  isCompleted: boolean;
}): boolean {
  return v.bookingCount === 0 && !v.isCompleted;
}

/**
 * The window the "is this employee busy" probe asks about.
 *
 * No procedure has been chosen yet, so there is no duration to ask with — only
 * the start time the operator clicked in the calendar. This asks about the
 * smallest slot the calendar draws (15 minutes) starting there: enough to catch
 * "that hour is already taken", not a promise that a two-hour procedure will
 * fit. The real check still runs in the booking panel on save.
 *
 * Returns null for anything that is not HH:mm, so a half-typed time asks
 * nothing rather than asking about 23:99.
 */
export function probeWindow(
  startTime: string | null | undefined,
  minutes = 15
): { startTime: string; endTime: string } | null {
  if (!startTime) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(startTime.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;

  const end = h * 60 + min + minutes;
  // A probe that would spill past midnight is clamped to the end of the day:
  // the calendar has no next-day column to conflict with anyway.
  const capped = Math.min(end, 23 * 60 + 59);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    startTime: `${pad(h)}:${pad(min)}`,
    endTime: `${pad(Math.floor(capped / 60))}:${pad(capped % 60)}`,
  };
}

/**
 * The options the header picker offers: everyone working that day, plus the
 * employee currently selected if they are not among them.
 *
 * Without that last part, moving the visit to a day the chosen employee does
 * not work leaves nz-select with a value and no matching option — it then
 * renders the raw uuid. Keeping the current one visible is also the honest
 * reading: they are still who this visit is for until the operator says
 * otherwise.
 */
export function employeeOptions(v: {
  workingToday: Array<{ id: string; fullName: string }>;
  current: { id: string; fullName: string } | null;
}): Array<{ id: string; fullName: string }> {
  const list = v.workingToday ?? [];
  if (!v.current) return list;
  return list.some((e) => e.id === v.current?.id)
    ? list
    : [v.current, ...list];
}
